import { MoreHorizontal } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { useAnchorReflow } from '@/components/ui/use-anchor-reflow';
import { cn } from '@/lib/utils';

// =============================================================================
// menu-button.tsx — Menu contextuel accessible (bouton « … » → menu d'actions).
// =============================================================================
// Widget « menu button » WAI-ARIA écrit à la main (aucune dépendance ajoutée :
// @radix-ui/react-dropdown-menu absent du lockfile figé). Utilisé pour le menu
// contextuel PAR LIGNE du registre des campagnes (DBU §2.5 l.1119).
//
// WCAG 2.1 AA / motif « menu button » :
//   - déclencheur : aria-haspopup="menu" + aria-expanded + aria-controls ;
//   - menu : role="menu" ; items : role="menuitem" ;
//   - clavier : Entrée/Espace/Flèche-bas ouvrent et focalisent le 1er item,
//     Flèche-haut ouvre et focalise le dernier ; dans le menu, Flèches Haut/Bas +
//     Home/End déplacent le focus (en sautant les items désactivés), Échap ferme
//     et REND le focus au déclencheur, Tab ferme ;
//   - clic hors du menu : ferme ;
//   - un item désactivé (aria-disabled) n'est ni focalisable ni activable ; il
//     porte un libellé texte explicite (la couleur n'est jamais seule porteuse).

export interface MenuAction {
  /** Libellé de l'action (porteur d'information, jamais la seule couleur). */
  readonly label: string;
  /** Déclenche l'action puis referme le menu. Ignoré si `disabled`. */
  readonly onSelect: () => void;
  /** Action indisponible (ex. duplication hors Clôturée/Archivée). */
  readonly disabled?: boolean;
}

export interface MenuButtonProps {
  /** Étiquette accessible du déclencheur (ex. « Actions pour la campagne X »). */
  readonly label: string;
  /** Items du menu (au moins un). */
  readonly items: readonly MenuAction[];
}

export function MenuButton({
  label,
  items,
}: MenuButtonProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = React.useId();
  // Position ANCRÉE au trigger : le menu est PORTALÉ dans <body> (position:fixed) pour ÉCHAPPER à
  // tout conteneur `overflow` (ex. `overflow-x-auto` d'une table, qui force overflow-y→auto et
  // ROGNE le menu de la dernière ligne). Capturée à l'ouverture ; le menu se ferme au scroll/resize.
  const [anchor, setAnchor] = React.useState<{
    readonly top: number;
    readonly right: number;
  } | null>(null);

  /** Ancre le menu (position:fixed) sous le trigger, aligné à droite. */
  const updateAnchor = React.useCallback((): void => {
    if (triggerRef.current === null) {
      return;
    }
    const r = triggerRef.current.getBoundingClientRect();
    setAnchor({ top: r.bottom + 4, right: Math.max(0, window.innerWidth - r.right) });
  }, []);

  React.useLayoutEffect(() => {
    if (open) {
      updateAnchor();
    }
  }, [open, updateAnchor]);

  const enabledIndexes = React.useMemo(
    () =>
      items.reduce<number[]>((acc, item, index) => {
        if (item.disabled !== true) {
          acc.push(index);
        }
        return acc;
      }, []),
    [items],
  );

  const close = React.useCallback((restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  const focusItem = React.useCallback((index: number): void => {
    itemRefs.current[index]?.focus();
  }, []);

  // Ouverture au clavier : focalise le 1er (ou dernier) item ENABLED après rendu.
  const openTo = React.useCallback(
    (edge: 'first' | 'last'): void => {
      if (enabledIndexes.length === 0) {
        return;
      }
      setOpen(true);
      const target =
        edge === 'first'
          ? enabledIndexes[0]
          : enabledIndexes[enabledIndexes.length - 1];
      // Le menu se rend au tick suivant : on diffère le focus.
      window.requestAnimationFrame(() => {
        if (target !== undefined) {
          focusItem(target);
        }
      });
    },
    [enabledIndexes, focusItem],
  );

  // Clic hors du composant : ferme (sans voler le focus). Le menu étant PORTALÉ hors de rootRef,
  // on l'exclut explicitement via menuRef (sinon un clic sur un menuitem serait « dehors » → le
  // menu se fermerait au mousedown AVANT que le onClick de l'item ne parte).
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      const inRoot = rootRef.current !== null && rootRef.current.contains(target);
      const inMenu = menuRef.current !== null && menuRef.current.contains(target);
      if (!inRoot && !inMenu) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  // Au scroll (capture, tout conteneur), au resize ET AU ZOOM navigateur, le menu est RÉ-ANCRÉ au
  // trigger, jamais fermé : le focus du trigger fait défiler un conteneur `overflow` qui le masquait en
  // partie, et une fermeture rendait alors le menu inouvrable (bug ST-CREATION-DUP, 2026-09-16). Le
  // volet « zoom » vient de U1-bis (menus flottants au zoom, retour GATE_HUMAN 2026-09-18).
  useAnchorReflow(open, updateAnchor);

  const onTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openTo('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openTo('last');
    }
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (enabledIndexes.length === 0) {
      return;
    }
    const activeIndex = itemRefs.current.findIndex(
      (el) => el === document.activeElement,
    );
    const posInEnabled = enabledIndexes.indexOf(activeIndex);
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        // Tab quitte le menu : ferme sans restaurer (le focus suit l'ordre naturel).
        setOpen(false);
        break;
      case 'ArrowDown': {
        event.preventDefault();
        const next = enabledIndexes[(posInEnabled + 1) % enabledIndexes.length];
        if (next !== undefined) {
          focusItem(next);
        }
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev =
          enabledIndexes[
            (posInEnabled - 1 + enabledIndexes.length) % enabledIndexes.length
          ];
        if (prev !== undefined) {
          focusItem(prev);
        }
        break;
      }
      case 'Home': {
        event.preventDefault();
        const first = enabledIndexes[0];
        if (first !== undefined) {
          focusItem(first);
        }
        break;
      }
      case 'End': {
        event.preventDefault();
        const last = enabledIndexes[enabledIndexes.length - 1];
        if (last !== undefined) {
          focusItem(last);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>

      {open && anchor !== null
        ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          // Le focus vit sur les menuitem (roving) ; le conteneur reste focusable
          // programmatiquement (tabIndex=-1) sans entrer dans l'ordre de tabulation.
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          // PORTALÉ dans <body>, position:fixed ancrée au trigger → échappe à tout `overflow`.
          style={{ position: 'fixed', top: anchor.top, right: anchor.right }}
          className={cn(
            'z-50 min-w-[14rem] rounded-md border border-border',
            'bg-card p-1 text-card-foreground shadow-md',
          )}
        >
          {items.map((item, index) => {
            const isDisabled = item.disabled === true;
            return (
              <button
                key={item.label}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-disabled={isDisabled || undefined}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }
                  setOpen(false);
                  item.onSelect();
                }}
                className={cn(
                  'block w-full rounded-sm px-3 py-2 text-left text-sm',
                  isDisabled
                    ? 'cursor-not-allowed text-muted-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
            document.body,
          )
        : null}
    </div>
  );
}
